import { useContext, useState, useMemo, useRef, useCallback } from 'react';
import { AppContext } from '@/context/AppContext';
import { Plus, Trash2, Search, Upload, X } from 'lucide-react';
import PageHeader from '@/components/shared/PageHeader';
import EmptyState from '@/components/shared/EmptyState';
import LoadingSpinner from '@/components/shared/LoadingSpinner';
import ConfirmDialog from '@/components/shared/ConfirmDialog';
import VoiceInput from '@/components/shared/VoiceInput';
import * as api from '@/services/api';

function parseCSVLine(line) {
  const result = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') { inQuotes = !inQuotes; }
    else if (ch === ',' && !inQuotes) { result.push(current.trim()); current = ''; }
    else { current += ch; }
  }
  result.push(current.trim());
  return result;
}

export default function KeywordManager() {
  const { 
    activeClientId, 
    activeClient,
    keywords, 
    loadKeywords, 
    setKeywords,
    addToast 
  } = useContext(AppContext);

  const [pendingKeywords, setPendingKeywords] = useState([]);
  const [inputText, setInputText] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [customLocation, setCustomLocation] = useState('');
  const [filter, setFilter] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const fileInputRef = useRef(null);
  const textInputRef = useRef(null);

  const clientKeywords = useMemo(() => {
    return keywords[activeClientId] || [];
  }, [keywords, activeClientId]);

  const locationOptions = useMemo(() => {
    return [...new Set(clientKeywords.map(k => k.location))].sort();
  }, [clientKeywords]);

  const filteredKeywords = useMemo(() => {
    return clientKeywords.filter(kw =>
      kw.keyword.toLowerCase().includes(filter.toLowerCase()) ||
      kw.location.toLowerCase().includes(filter.toLowerCase())
    ).sort((a, b) => a.keyword.localeCompare(b.keyword));
  }, [clientKeywords, filter]);

  const getResolvedLocation = useCallback(() => {
    return selectedLocation === 'custom' ? customLocation.trim() : selectedLocation;
  }, [selectedLocation, customLocation]);

  const addTagsFromText = useCallback((text) => {
    const parts = text.split(/[,\n]/).map(s => s.trim()).filter(Boolean);
    if (parts.length === 0) return;
    const loc = getResolvedLocation();
    const newTags = parts.map(keyword => ({ keyword, location: loc || '' }));
    setPendingKeywords(prev => {
      const existing = new Set(prev.map(p => `${p.keyword}|||${p.location}`));
      const unique = newTags.filter(t => !existing.has(`${t.keyword}|||${t.location}`));
      return [...prev, ...unique];
    });
  }, [getResolvedLocation]);

  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const text = e.key === ',' ? inputText : inputText;
      if (text.trim()) {
        addTagsFromText(text);
        setInputText('');
      }
    } else if (e.key === 'Backspace' && !inputText && pendingKeywords.length > 0) {
      setPendingKeywords(prev => prev.slice(0, -1));
    }
  };

  const handleInputPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text');
    if (pasted.includes(',') || pasted.includes('\n')) {
      addTagsFromText(pasted);
      setInputText('');
    } else {
      setInputText(prev => prev + pasted);
    }
  };

  const removeTag = (index) => {
    setPendingKeywords(prev => prev.filter((_, i) => i !== index));
  };

  const handleCSVUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const lines = text.split(/\r?\n/).filter(l => l.trim());
      if (lines.length === 0) return;

      const headerLine = lines[0].toLowerCase();
      const hasHeader = headerLine.includes('keyword') || headerLine.includes('location');
      const startIdx = hasHeader ? 1 : 0;

      const parsed = [];
      for (let i = startIdx; i < lines.length; i++) {
        const cols = parseCSVLine(lines[i]);
        const keyword = cols[0]?.trim();
        if (!keyword) continue;
        const location = cols[1]?.trim() || getResolvedLocation() || '';
        const tag = cols[2]?.trim() || undefined;
        parsed.push({ keyword, location, ...(tag ? { tag } : {}) });
      }

      if (parsed.length === 0) {
        addToast('No keywords found in CSV', 'error');
        return;
      }

      setPendingKeywords(prev => {
        const existing = new Set(prev.map(p => `${p.keyword}|||${p.location}`));
        const unique = parsed.filter(t => !existing.has(`${t.keyword}|||${t.location}`));
        return [...prev, ...unique];
      });
      addToast(`${parsed.length} keywords loaded from CSV`, 'success');
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleVoiceResult = (result) => {
    const text = Array.isArray(result) ? result.join(', ') : result;
    addTagsFromText(text);
  };

  const handleAddKeywords = async () => {
    // Add any remaining text in the input
    let finalKeywords = [...pendingKeywords];
    const loc = getResolvedLocation();

    if (inputText.trim()) {
      const parts = inputText.split(/[,\n]/).map(s => s.trim()).filter(Boolean);
      const existing = new Set(finalKeywords.map(p => `${p.keyword}|||${p.location}`));
      parts.forEach(keyword => {
        const key = `${keyword}|||${loc || ''}`;
        if (!existing.has(key)) {
          finalKeywords.push({ keyword, location: loc || '' });
          existing.add(key);
        }
      });
    }

    // Apply selected location to any tags that don't have one
    if (loc) {
      finalKeywords = finalKeywords.map(k => ({
        ...k,
        location: k.location || loc,
      }));
    }

    const missingLocation = finalKeywords.some(k => !k.location);
    if (finalKeywords.length === 0) {
      addToast('Add at least one keyword', 'error');
      return;
    }
    if (missingLocation) {
      addToast('Select a location for all keywords', 'error');
      return;
    }

    setIsLoading(true);
    const { data, error } = await api.addKeywords(activeClientId, finalKeywords);
    
    if (error) {
      addToast(`Error adding keywords: ${error}`, 'error');
    } else {
      const count = Array.isArray(data) ? data.length : finalKeywords.length;
      addToast(`${count} keywords added successfully`, 'success');
      setPendingKeywords([]);
      setInputText('');
      setCustomLocation('');
      await loadKeywords(activeClientId);
    }
    setIsLoading(false);
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setIsLoading(true);
    const { error } = await api.deleteKeyword(activeClientId, confirmDelete.id);
    if (error) {
      addToast(`Error deleting keyword: ${error}`, 'error');
    } else {
      addToast('Keyword deleted', 'success');
      setKeywords(prev => ({
        ...prev,
        [activeClientId]: prev[activeClientId].filter(kw => kw.id !== confirmDelete.id)
      }));
    }
    setConfirmDelete(null);
    setIsLoading(false);
  };

  const hasCSVLocations = pendingKeywords.some(k => k.location && getResolvedLocation() !== k.location);

  return (
    <div data-testid="keyword-manager">
      <PageHeader label="Keywords" />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Add Keywords Panel */}
        <div className="lg:col-span-1 card p-4">
          <h3 className="text-base font-bold font-syne text-[#E2E8F0] mb-4">Add New Keywords</h3>
          
          <div className="space-y-4">
            {/* Tag Input + CSV Upload */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-mono text-[#94A3B8]">Keywords</label>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center gap-1 text-[10px] font-mono text-[#0070F3] hover:text-[#60A5FA] transition-colors"
                    data-testid="csv-upload-btn"
                  >
                    <Upload size={12} />
                    Import CSV
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    onChange={handleCSVUpload}
                    className="hidden"
                    data-testid="csv-file-input"
                  />
                  <VoiceInput 
                    onResult={handleVoiceResult} 
                    fieldType="keywords" 
                  />
                </div>
              </div>
              
              {/* Tag chips area */}
              <div
                className="min-h-[120px] max-h-[240px] overflow-y-auto bg-[#0A0F1E] border border-[#1E2D45] rounded-lg p-2 focus-within:border-[#0070F3] transition-colors cursor-text"
                onClick={() => textInputRef.current?.focus()}
                data-testid="keyword-tag-area"
              >
                <div className="flex flex-wrap gap-1.5">
                  {pendingKeywords.map((kw, i) => (
                    <span
                      key={i}
                      className="inline-flex items-center gap-1 bg-[#1A2338] border border-[#1E2D45] text-[#E2E8F0] text-xs font-mono rounded-md pl-2 pr-1 py-1 group hover:border-[#0070F3] transition-colors"
                      data-testid={`keyword-tag-${i}`}
                    >
                      <span className="break-words">{kw.keyword}</span>
                      {kw.location && (
                        <span className="text-[#4A5568] text-[9px] ml-0.5">({kw.location})</span>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); removeTag(i); }}
                        className="p-0.5 text-[#4A5568] hover:text-[#EF4444] transition-colors"
                        data-testid={`remove-tag-${i}`}
                      >
                        <X size={10} />
                      </button>
                    </span>
                  ))}
                  <input
                    ref={textInputRef}
                    type="text"
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                    onKeyDown={handleInputKeyDown}
                    onPaste={handleInputPaste}
                    placeholder={pendingKeywords.length === 0 ? "Type keywords, separate by comma..." : ""}
                    className="flex-1 min-w-[120px] bg-transparent text-sm text-[#E2E8F0] placeholder-[#4A5568] outline-none py-1"
                    data-testid="keyword-tag-input"
                  />
                </div>
              </div>
              <p className="text-[10px] font-mono text-white mt-1">
                Separate with comma, Enter, or paste. Import CSV (columns: keyword, location).
              </p>
            </div>

            {/* Location selector — shown only if no CSV locations */}
            {!hasCSVLocations && (
              <div>
                <label className="block text-sm font-mono text-[#94A3B8] mb-1">Location</label>
                {locationOptions.length > 0 ? (
                  <select
                    value={selectedLocation}
                    onChange={(e) => setSelectedLocation(e.target.value)}
                    className="select"
                    data-testid="location-select"
                  >
                    <option value="">Select location...</option>
                    {locationOptions.map(loc => (
                      <option key={loc} value={loc}>{loc}</option>
                    ))}
                    <option value="custom">+ Add new location</option>
                  </select>
                ) : (
                  <select
                    value="custom"
                    onChange={(e) => setSelectedLocation(e.target.value)}
                    className="select"
                  >
                    <option value="custom">Enter new location</option>
                  </select>
                )}
              </div>
            )}

            {/* Custom location input */}
            {!hasCSVLocations && (selectedLocation === 'custom' || locationOptions.length === 0) && (
              <div>
                <label className="block text-sm font-mono text-[#94A3B8] mb-1">New Location</label>
                <input
                  type="text"
                  placeholder="e.g., Atlanta GA"
                  value={customLocation}
                  onChange={(e) => setCustomLocation(e.target.value)}
                  className="input-field"
                  data-testid="custom-location-input"
                />
              </div>
            )}

            {/* Preview count */}
            {pendingKeywords.length > 0 && (
              <div className="flex items-center justify-between text-xs font-mono">
                <span className="text-[#94A3B8]">
                  {pendingKeywords.length} keyword{pendingKeywords.length !== 1 ? 's' : ''} ready
                </span>
                <button
                  onClick={() => setPendingKeywords([])}
                  className="text-[#EF4444] hover:text-[#F87171] transition-colors"
                  data-testid="clear-all-tags"
                >
                  Clear all
                </button>
              </div>
            )}

            {/* Add button */}
            <button
              onClick={handleAddKeywords}
              disabled={isLoading || (pendingKeywords.length === 0 && !inputText.trim())}
              className="button-primary w-full flex items-center justify-center gap-2"
              data-testid="add-keywords-btn"
            >
              {isLoading ? <LoadingSpinner size="sm" /> : <Plus size={16} />}
              Add Keywords
            </button>
          </div>
        </div>

        {/* Keywords Table */}
        <div className="lg:col-span-2 card p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <h3 className="text-sm sm:text-base font-bold font-syne text-[#E2E8F0]">
              Tracked Keywords ({filteredKeywords.length})
            </h3>
            <div className="relative w-full sm:w-auto">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4A5568] pointer-events-none z-10" />
              <input
                type="text"
                placeholder="Search keywords..."
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="w-full sm:w-64 bg-[#0A0F1E] border border-[#1E2D45] rounded-lg py-2 pl-10 pr-3 text-sm text-[#E2E8F0] placeholder-[#4A5568] focus:border-[#0070F3] focus:outline-none"
                data-testid="keyword-search"
              />
            </div>
          </div>

          {filteredKeywords.length === 0 ? (
            <EmptyState
              icon="🔑"
              title="No Keywords Tracked"
              message="Add keywords above to start tracking their rankings."
            />
          ) : (
            <>
              {/* Mobile: Card Layout */}
              <div className="sm:hidden space-y-3">
                {filteredKeywords.map(kw => (
                  <div 
                    key={kw.id}
                    className="flex items-center justify-between p-3 bg-[#0A0F1E] rounded-lg border border-[#1E2D45]"
                    data-testid={`keyword-row-${kw.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <span className="text-sm font-syne font-semibold text-[#E2E8F0] block truncate">
                        {kw.keyword}
                      </span>
                      <span className="text-xs text-[#94A3B8]">{kw.location}</span>
                    </div>
                    <button
                      onClick={() => setConfirmDelete(kw)}
                      className="p-2 text-[#4A5568] hover:text-[#EF4444] transition-colors flex-shrink-0"
                      data-testid={`delete-keyword-${kw.id}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>

              {/* Desktop: Table Layout */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-[10px] font-mono uppercase text-[#4A5568] border-b border-[#1E2D45]">
                      <th className="py-3 pr-4">Keyword</th>
                      <th className="py-3 pr-4">Location</th>
                      <th className="py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredKeywords.map(kw => (
                      <tr 
                        key={kw.id} 
                        className="border-b border-[#1E2D45] last:border-b-0"
                        data-testid={`keyword-row-desktop-${kw.id}`}
                      >
                        <td className="py-3 pr-4">
                          <span className="text-sm font-syne font-semibold text-[#E2E8F0]">
                            {kw.keyword}
                          </span>
                        </td>
                        <td className="py-3 pr-4">
                          <span className="text-sm text-[#94A3B8]">{kw.location}</span>
                        </td>
                        <td className="py-3 text-right">
                          <button
                            onClick={() => setConfirmDelete(kw)}
                            className="p-2 text-[#4A5568] hover:text-[#EF4444] transition-colors"
                            data-testid={`delete-keyword-desktop-${kw.id}`}
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Delete Confirmation */}
      {confirmDelete && (
        <ConfirmDialog
          message={`Are you sure you want to delete "${confirmDelete.keyword}" for ${confirmDelete.location}?`}
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
}
